<template>
  <div class="container-fluid analytics-page mb-6 mb-md-0">
    <div class="row">
      <div class="col-12">
        <date-based-page-header
          v-model="curDate"
          class="analytics-page-header"
          calendar-unit="monthly"
          today-button-label="이번 달"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title">
              <span style="margin-right: 5px;">
                <strong>{{ presentDateWords }}</strong> 회원권 매출
              </span>

              <popover>
                결제일 기준 당월 회원권이 결제된 총 금액과 <br> 신규/재등록 회원 별 매출액 및 구성 비율입니다. <br> 삭제된 회원권은 총계에서 제외됩니다.
              </popover>
            </h4>
          </div>

          <div class="monthly-sales-chart">
            <div class="circle-chart-container">
              <span class="circle-chart__indicator">
                {{ presentSumOfSales }}
              </span>
              <canvas
                v-circle-chart="presentCircleChartData"
              />
            </div>

            <div class="circle-chart__legend">
              <ul>
                <li>
                  <div class="legend-field">
                    <div class="legend-circle" /> 신규
                  </div>
                  <div class="legend-payment">
                    {{ presentPrice(membershipSalesByClassification[0]) }}
                  </div>
                </li>
                <li>
                  <div class="legend-field">
                    <div class="legend-circle reenlist" /> 재등록
                  </div>
                  <div class="legend-payment">
                    {{ presentPrice(membershipSalesByClassification[1]) }}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6 col-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title">
              <span style="margin-right: 5px;">
                <strong>{{ presentDateWords }}</strong> 회원권 <strong>판매 랭킹</strong>
              </span>

              <popover>
                결제일 기준 당월 회원권 판매 순위와 해당 회원권의 <br> 판매 수입니다. 삭제된 회원권은 제외됩니다.
              </popover>
            </h4>
          </div>

          <MembershipSalesRank
            :cur-date="curDate"
            :sold-memberships="membershipSalesRanks"
          />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <membership-sales-table
          :cur-date="curDate"
          :title-date="presentDateWords"
        />
      </div>
    </div>
  </div>
</template>

<script>
import circleChart from '@/directives/CircleChart.js';
import DateBasedPageHeader from '../../components/DateBasedPageHeader';
import MembershipSalesRank from './MembershipSalesRank';
import MembershipSalesTable from './MembershipSalesTable';
import { computedGetters } from '../../utils/vuex-helper';
import _ from 'lodash';
import Popover from '../../components/popover';

const priceDigitSplitRegexp = /\B(?=(\d{3})+(?!\d))/g;

function presentPrice(price) {
  if (Number.isInteger(price)) {
    return price.toString().replace(priceDigitSplitRegexp, ',') + '원';
  }
  ('- 원');
}

export default {
  name: 'AnalyticsPage',
  components: {
    Popover,
    MembershipSalesTable,
    MembershipSalesRank,
    DateBasedPageHeader
  },
  directives: {
    circleChart
  },
  data() {
    return {
      curDate: new Date()
    };
  },
  watch: {
    curDate: {
      immediate: false,
      handler(date) {
        this.$store.dispatch('syncPayments', {
          date: date
        });
      }
    },
    isAnalyticsMenuVisible: {
      immediate: true,
      handler: function(isAccessAllowed) {
        if (!isAccessAllowed) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    }
  },
  async created() {
    await this.$store.dispatch('syncParentMemberships');
    await this.$store.dispatch('syncPayments', {
      date: this.curDate
    });
  },
  computed: {
    ...computedGetters([
      'membershipSalesRanks',
      'membershipSalesByClassification',
      'isAnalyticsMenuVisible'
    ]),
    presentDateWords() {
      return this.$formatDate(this.curDate, 'YYYY년 M월');
    },
    presentCircleChartData() {
      return {
        labels: ['신규', '재등록'],
        data: this.membershipSalesByClassification,
        backgroundColor: ['#494949', '#cbcbcb'],
        options: {
          isLegendVisible: false
        }
      };
    },
    presentSumOfSales() {
      return presentPrice(_.sum(this.membershipSalesByClassification));
    }
  },
  methods: {
    presentPrice
  }
};
</script>

<style lang="scss">
.analytics-page-header {
  button.el-button.calendar.word-btn {
    padding: 12px 16px;
  }
}

.metric {
  border-radius: 6px;
  background-color: #f2f2f2;
  padding: 15px 20px;
  color: #4a4a4a;

  h5 {
    font-size: 12px;
    letter-spacing: -0.5px;
    margin-bottom: 2px;
  }

  .indicator {
    line-height: 29px;
    font-size: 20px;
    letter-spacing: -0.8px;
    margin-bottom: 2px;
  }

  .delta {
    font-weight: normal;
    font-size: 15px;
    line-height: 22px;
    color: #9b9b9b;

    &.increase {
      color: #6bc01e;
    }

    &.decrease {
      color: #ff0000;
    }
  }
}

.monthly-sales-chart {
  height: 450px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.circle-chart-container {
  margin: 30px auto 38px;
  width: 270px;
  height: 270px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .circle-chart__indicator {
    font-size: 28px;
    letter-spacing: -1.1px;
    text-align: center;
    color: #4a4a4a;
  }

  canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 270px;
    height: 270px;
  }
}

.circle-chart__legend {
  display: inline-block;

  ul {
    margin: 0 auto;
    padding: 0;
    list-style-type: none;

    li {
      line-height: 30px;
      vertical-align: center;
      margin-bottom: 7px;

      .legend-field {
        vertical-align: middle;
        width: 75px;
        display: inline-block;

        font-size: 13px;
        font-weight: 500;
        letter-spacing: -0.7px;
        color: #4a4a4a;

        div.legend-circle {
          width: 10px;
          height: 10px;
          background-color: #494949;
          border-radius: 10px;
          display: inline-block;
          margin-right: 5px;

          &.reenlist {
            background-color: #f2f2f2;
          }
        }
      }

      .legend-payment {
        vertical-align: middle;
        display: inline-block;
        font-size: 20px;
        font-weight: normal;
        letter-spacing: -1px;
        color: #4a4a4a;
      }
    }
  }
}
</style>
